import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { Avatar } from '@mui/material';
import { get } from 'lodash';

import { VERBIAGE } from 'constants/verbiage';
import * as SHIPMENT_STATUS from 'constants/shipmentStatus';
import { translateEvents, renderDeliveryTimeAndLabel } from 'utils/events';

const PREFIX = 'Overview';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  logo: `${PREFIX}-logo`,
  status: `${PREFIX}-status`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  uppercase: `${PREFIX}-uppercase`,
  redText: `${PREFIX}-redText`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
    padding: '2rem 0 1rem',
    [theme.breakpoints.down('sm')]: {
      gap: '1rem',
      gridTemplateColumns: '1fr',
    },
  },

  [`& .${classes.title}`]: {
    margin: 0,
    color: theme.colorTokens.onSurfaceTextTitle,
    fontWeight: 500,
    textDecoration: 'underline',
  },

  [`& .${classes.logo}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },

  [`& .${classes.status}`]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },

  [`& .${classes.label}`]: {
    margin: '0 0 0.25rem 0',
    color: theme.colorTokens.onSurfaceTextLabel,
    fontSize: '14px',
  },

  [`& .${classes.value}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
    fontSize: '14px',
    fontWeight: 700,
  },

  [`& .${classes.uppercase}`]: {
    textTransform: 'uppercase',
  },

  [`& .${classes.redText}`]: {
    color: theme.palette.error.main,
  },
}));

export const Overview = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const { deliveryStore } = store;
  const { delivery, settings, hideCompanyBranding } = deliveryStore;
  const [enableRedPastDate, setEnableRedPastDate] = useState(false);

  useEffect(() => {
    const highlightPastDate = get(deliveryStore, 'settings.highlight_past_delivery_date');
    setEnableRedPastDate(highlightPastDate === 'true');
  }, [deliveryStore]);

  if (!delivery || !settings) return null;

  const { client, shipment, outbound_events, no_service_dates } = delivery;
  const translatedEvents = translateEvents(outbound_events || [], shipment);
  const milestones = translatedEvents.filter((e) => VERBIAGE[e.convertedSignal]).map((e) => VERBIAGE[e.convertedSignal].milestone);
  const milestone = milestones[0] || SHIPMENT_STATUS.PROCESSING;
  const status = renderDeliveryTimeAndLabel(milestone, translatedEvents, shipment, settings, no_service_dates);

  const shouldHighlightDate = enableRedPastDate && status.isPastDate;

  return (
    <Root>
      <h3 className={classes.title}>Shipment Details</h3>
      <div className={classes.container}>
        <div>
          {hideCompanyBranding ? null : (
            <div className={classes.logo}>
              <Avatar src={client.logo_url} variant="square" alt={client.company} />
              <span data-testid="client-name">{client.company}</span>
            </div>
          )}
        </div>
        <div>
          <p className={classes.label}>Status:</p>
          <span className={clsx(classes.value, classes.uppercase)} data-testid="shipment-status">{milestone}</span>
        </div>
        <div>
          <p className={classes.label}>{status.label}</p>
          <span className={clsx(classes.value, { [classes.redText]: shouldHighlightDate })} data-testid="shipment-time">{status.time}</span>
        </div>
      </div>
    </Root>
  );
});
